<style lang="scss">
  @use "../../../../../assets/css/global.scss" as *;
  @use "../../../../../assets/css/_colors.scss" as *;
  .dropdown {
    width: fit-content;
    display: flex;
    position: relative;
    user-select: none;

    &Label {
      display: flex;
      align-items: center;
    }

    &.fullWidth {
      width: 100%;
    }

    .dropdownButton {
      $gap: 0.5rem;

      &.fullWidth {
        width: 100%;
      }

      width: fit-content;
      align-items: center;
      display: flex;
      font-family: inherit;
      gap: $gap;
      justify-content: space-between;

      &:not(:disabled) {
        cursor: pointer;
      }

      &::after {
        content: "\f107";
        font-size: 1.2rem;
        font-family: "Font Awesome 6 Pro";
        font-weight: 300;
      }

      &:not(.naked) {
        background-color: white;
        // border: 2px solid $light-gray;
        border: 1.5px solid $light-gray;
        border-radius: 0.375rem;
        color: $text-black;
        padding: 0.5rem 1rem; // TAG for dropdown sizing issue??
        // padding: 0.35rem 0.7rem;
        // font-size: 0.75rem;

        @extend .text-body-2-bold;

        &:hover {
          &:not(:disabled) {
            background-color: $light-gray;
          }
        }
      }

      &.naked {
        background-color: transparent;
        border: 0;
        color: $primary-color;
        padding: 0;

        text-wrap: nowrap;

        &:hover {
          color: #4e73df;
        }
      }

      &.isOpen {
        cursor: default;

        &:not(.naked) {
          background-color: $light-gray;
        }

        &.naked {
          &::after {
            transform: rotate(180deg);
          }
        }
      }

      &Placeholder {
        align-items: center;
        display: flex;
        gap: $gap;
      }
    }

    .dropdownMenu {
      width: auto;

      &.fullWidth {
        width: 100%;
        max-width: none;
      }

      max-height: calc(min(36rem, 90vh));
      background-color: white;
      border-radius: 0.625rem;
      box-shadow: $subtle-shadow;
      overflow-y: auto;
      padding: 0.75rem;
      position: absolute;
      right: 0;
      top: calc(100% + 0.5rem);
      white-space: nowrap;
      z-index: 99;

      &.dropup {
        top: auto;
        bottom: calc(100% + 0.5rem);
      }

      @media (max-width: 1700px) {
        // Prevents the dropdown being cut-off at smaller widths
        &.rightAuto {
          right: auto;
        }
      }
    }
  }
</style>

<script>
  import { createEventDispatcher } from "svelte";
  import { handleDropdownOpen } from "../../../pages/Dashboard/utils/dropdowns";

  import SearchInput from "../SearchInput.svelte";
  import DropdownOption from "./DropdownOption.svelte";
  import DropdownSelectedOption from "./DropdownSelectedOption.svelte";

  export let options = [];
  export let multiple = false;
  export let searchable = false;
  export let selected = [];
  export let selectAll = true;
  export let selectAllText = "Select All";
  export let allSelectedText = null;
  export let placeholder = "Select an option";
  export let displayInitials = false;
  export let fullWidth = false;
  export let dropdownMenuMaxWidth = "20rem";
  export let naked = false;
  export let confirmDropdownOnClose = false;
  export let inactive = false;
  export let reactToEachSelection = false;
  export let dropdownLabel = "";
  export let calculateSecondaryLabels = false;
  export let secondaryLabelKey = "";
  export let dropup = false;
  export let callback = () => {};
  export let truncateLength = 0;
  export let rightAuto = false;

  export let isOpen = false;

  const dispatch = createEventDispatcher();

  let searchTerm = "";
  let dropdownRef;

  let groupByLabel = {};
  if (calculateSecondaryLabels && secondaryLabelKey) {
    options.forEach((option) => {
      if (groupByLabel[option.label]) {
        groupByLabel[option.label].push(option);
      } else {
        groupByLabel[option.label] = [option];
      }
    });
  }

  const handleOutsideClick = (event) => {
    if (dropdownRef && !dropdownRef.contains(event.target)) {
      isOpen = false;
    }
  };

  const handleSearch = (event) => {
    searchTerm = event.target.value;
  };

  const toggleSingleOption = (option) => {
    selected = option;
    isOpen = false;

    // TODO - empty function is truthy so this will always get called unnecessarily
    if (callback) {
      callback(selected);
    }
  };

  const toggleMultiOption = (option) => {
    const selectedOption = selected.find((item) => item.value === option.value);

    selected = selectedOption
      ? selected.filter((item) => item !== selectedOption)
      : [...selected, option];

    if (reactToEachSelection) dispatch("selection-event", selected);
  };

  const toggleOption = (option) => {
    if (multiple) toggleMultiOption(option);
    else toggleSingleOption(option);
  };

  $: filteredOptions = options.filter((option) =>
    (option.label || option.value || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const toggleSelectAll = () => {
    selected =
      selected.length === filteredOptions.length ? [] : [...filteredOptions];

    if (callback) callback(selected);

    if (reactToEachSelection) dispatch("selection-event", selected);
  };

  const toggleDropdown = () => {
    if (!isOpen) handleDropdownOpen({ close: () => (isOpen = false) });
    isOpen = !isOpen;
  };

  let pendingDropdownConfirmation = false;
  let formattedSelected = [];

  $: {
    if (isOpen) {
      window.addEventListener("click", handleOutsideClick);
      if (confirmDropdownOnClose) {
        if (!pendingDropdownConfirmation) {
          pendingDropdownConfirmation = true;
        }
      }
    } else {
      window.removeEventListener("click", handleOutsideClick);
      if (searchable && searchTerm) {
        searchTerm = "";
      }
      if (confirmDropdownOnClose && pendingDropdownConfirmation) {
        dispatch("dropdown-confirmation", formattedSelected);
        pendingDropdownConfirmation = false;
      }
    }
  }

  $: displayAllSelectedText =
    multiple && selected.length === filteredOptions.length && allSelectedText;

  $: {
    if (Array.isArray(selected)) {
      formattedSelected = selected;
    } else if (selected && typeof selected === "object") {
      formattedSelected = [{ ...selected }];
    } else {
      formattedSelected = [];
    }
  }

  $: isOptionSelected = (option) => {
    if (multiple) {
      return formattedSelected.some((item) => item.value === option.value);
    }
    return formattedSelected[0].value === option.value;
  };
</script>

<div class="dropdown" class:fullWidth bind:this={dropdownRef}>
  {#if dropdownLabel}
    <span class="dropdownLabel"
      ><p style="margin-bottom: 0rem">{dropdownLabel}&nbsp;</p></span
    >
  {/if}
  <button
    class="dropdownButton"
    class:naked
    class:isOpen
    class:fullWidth
    disabled={inactive}
    on:click|stopPropagation={toggleDropdown}
  >
    <div class="dropdownButtonPlaceholder">
      {#if selected.length === 0}
        {placeholder}
      {:else if Array.isArray(selected)}
        <DropdownSelectedOption
          selectedOption={selected[0]}
          nSelected={selected.length}
          {allSelectedText}
          {displayAllSelectedText}
          {truncateLength}
        />
      {:else}
        <DropdownSelectedOption selectedOption={selected} />
      {/if}
    </div>
  </button>

  {#if isOpen}
    <div
      class="dropdownMenu"
      class:fullWidth
      class:multiple
      class:dropup
      class:rightAuto
      style={fullWidth ? "" : `max-width: ${dropdownMenuMaxWidth}`}
    >
      {#if searchable && !dropup}
        <SearchInput onInput={handleSearch} style="margin-bottom:0.5rem" />
      {/if}
      {#if multiple && selectAll}
        <!-- formatOption to take into account secondary label -->
        <DropdownOption
          classList="dropdownOptionSelectAll"
          option={{ value: "selectAll", label: selectAllText }}
          isSelected={selected.length === filteredOptions.length}
          {displayInitials}
          {multiple}
          on:click={toggleSelectAll}
        />
      {/if}
      {#each filteredOptions as option (option.value)}
        <DropdownOption
          {option}
          {multiple}
          isSelected={isOptionSelected(option)}
          {displayInitials}
          addSecondaryLabel={groupByLabel[option.label]?.length > 1}
          {secondaryLabelKey}
          on:click={() => toggleOption(option)}
        />
      {/each}
      {#if searchable && dropup}
        <SearchInput onInput={handleSearch} style="margin-top:0.5rem" />
      {/if}
    </div>
  {/if}
</div>
